/**
 * The range-filter buckets live in `lib/search-filters` — the same module
 * that maps them into a backend query, so the picker and the mapper can
 * never disagree about which ids exist. Re-exported here so component-side
 * imports stay in one place, and because dependencies point from
 * `new_components/` down into `lib/`, not the other way.
 */
export {
  AKTIVA_RANGES,
  EMPLOYEE_RANGES,
  FINANCIAL_RANGES,
  personAgeRanges,
  PROFIT_RANGES,
  type RangeDefinition,
} from '@/lib/search-filters';

import { COMPANY_COUNTRIES, GERMAN_REGION_NAMES, isCompanyCountryCode } from '@/lib/countries';

/**
 * Drop Bundesland/canton selections the given country scope can't match —
 * CH + Bayern would silently return nothing. '' (global) keeps everything.
 */
export function filterLocationsForCountry(
  values: string[],
  country: string,
): string[] {
  if (isCompanyCountryCode(country)) {
    const regions = new Set(COMPANY_COUNTRIES[country].regions);
    return values.filter((v) => regions.has(v));
  }
  return values;
}

/**
 * Order dropdown options by how many companies each would return, biggest
 * first — the ordering the city list has always had and the only useful one
 * for a vocabulary with no inherent sequence (states, legal forms, registers).
 * Filters whose values *do* have a natural order — revenue and the other
 * range bands, fiscal years, the WZ tree, the size and legal-status ladders —
 * must keep it and are deliberately not run through this.
 *
 * `countOf` returns undefined when the facet has not loaded yet or holds no
 * row for the value. Those sink below the counted ones but keep their
 * relative order, so before facets arrive the list renders in its original
 * (alphabetical) order rather than jumping around — Array.prototype.sort is
 * stable, which is what makes that true.
 */
export function sortByFacetCount<T>(
  items: readonly T[],
  countOf: (item: T) => number | undefined,
): T[] {
  return [...items].sort((a, b) => (countOf(b) ?? -1) - (countOf(a) ?? -1));
}

export const bundeslandList = GERMAN_REGION_NAMES;

/**
 * Register courts (Registergerichte), spelled exactly as `mv_search`
 * stores them — the search filter is an equality match, so any other
 * spelling silently returns nothing.
 *
 * This is the seed for the court dropdown so it renders immediately;
 * /api/search/filters/courts then replaces it with the live list from the
 * MV (also ordered by company count). Kept in sync with the 125 values
 * present on prod as of 2026-08-10. Bremerhaven, Kerpen, Langenfeld and
 * Schleiden used to be listed here and were removed: they are defunct
 * courts that hold no companies, so they could only ever return zero.
 */
export const courtList = [
  'Aachen',
  'Amberg',
  'Ansbach',
  'Arnsberg',
  'Aschaffenburg',
  'Augsburg',
  'Aurich',
  'Bad Hersfeld',
  'Bad Homburg v.d.H.',
  'Bad Kreuznach',
  'Bad Oeynhausen',
  'Bamberg',
  'Bayreuth',
  'Berlin (Charlottenburg)',
  'Bielefeld',
  'Bochum',
  'Bonn',
  'Braunschweig',
  'Bremen',
  'Chemnitz',
  'Coburg',
  'Coesfeld',
  'Cottbus',
  'Darmstadt',
  'Deggendorf',
  'Dortmund',
  'Dresden',
  'Duisburg',
  'Düren',
  'Düsseldorf',
  'Eschwege',
  'Essen',
  'Flensburg',
  'Frankfurt am Main',
  'Frankfurt/Oder',
  'Freiburg',
  'Friedberg',
  'Fritzlar',
  'Fulda',
  'Fürth',
  'Gelsenkirchen',
  'Gießen',
  'Göttingen',
  'Gütersloh',
  'Hagen',
  'Hamburg',
  'Hamm',
  'Hanau',
  'Hannover',
  'Hildesheim',
  'Hof',
  'Homburg',
  'Ingolstadt',
  'Iserlohn',
  'Jena',
  'Kaiserslautern',
  'Kassel',
  'Kempten (Allgäu)',
  'Kiel',
  'Kleve',
  'Koblenz',
  'Köln',
  'Königstein',
  'Korbach',
  'Krefeld',
  'Landau',
  'Landshut',
  'Lebach',
  'Leipzig',
  'Lemgo',
  'Limburg',
  'Lübeck',
  'Ludwigshafen a.Rhein (Ludwigshafen)',
  'Lüneburg',
  'Mainz',
  'Mannheim',
  'Marburg',
  'Memmingen',
  'Merzig',
  'Mönchengladbach',
  'Montabaur',
  'München',
  'Münster',
  'Neubrandenburg',
  'Neunkirchen',
  'Neuruppin',
  'Neuss',
  'Nürnberg',
  'Offenbach am Main',
  'Oldenburg (Oldenburg)',
  'Osnabrück',
  'Ottweiler',
  'Paderborn',
  'Passau',
  'Pinneberg',
  'Potsdam',
  'Recklinghausen',
  'Regensburg',
  'Rostock',
  'Saarbrücken',
  'Saarlouis',
  'Schweinfurt',
  'Schwerin',
  'Siegburg',
  'Siegen',
  'Stadthagen',
  'Steinfurt',
  'Stendal',
  'St. Ingbert (St Ingbert)',
  'Stralsund',
  'Straubing',
  'Stuttgart',
  'St. Wendel (St Wendel)',
  'Tostedt',
  'Traunstein',
  'Ulm',
  'Völklingen',
  'Walsrode',
  'Weiden i. d. OPf.',
  'Wetzlar',
  'Wiesbaden',
  'Wittlich',
  'Wuppertal',
  'Würzburg',
  'Zweibrücken',
];

/**
 * Legal forms offered in the Rechtsform filter, mirroring the backend's
 * `common_rechtsform` (backend/parser/xml/mappings.py) — keep the two in
 * step, since that list also drives the v1 API's `Rechtsform` enum.
 *
 * Ordered by how common the form is. Company counts are prod
 * measurements from 2026-08-10. UG and GmbH & Co. KG were missing here
 * until then, which meant the two forms users ask for most often were the
 * two they could not pick.
 *
 * One caveat when reading results: the register's coding is coarser than
 * these labels suggest. Of the 254,771 companies coded `112100` ("KG"),
 * 199,021 are named "… GmbH & Co. KG". So "GmbH & Co. KG" returns only
 * the correctly-coded ones (~12% of the real population) and the rest sit
 * under "KG". Separating them properly needs a name-derived
 * reclassification, not a longer option list.
 */
export type RechtsformOption = {
  codes: string[];
  label: string;
  /** Extra search terms for the combobox (cmdk `keywords`). Swiss (CH-HR)
   *  companies all carry code 690000, so their register spellings (S.A.,
   *  Sàrl, …) surface "Ausländische Rechtsform", never the German buckets. */
  keywords?: string[];
};

const GERMAN_RECHTSFORM_OPTIONS: RechtsformOption[] = [
  { codes: ['221110'], label: 'GmbH' },
  { codes: ['112100'], label: 'KG', keywords: ['Kommanditgesellschaft'] },
  { codes: ['411000'], label: 'e.K.', keywords: ['Einzelunternehmen', 'Einzelfirma'] },
  { codes: ['123000'], label: 'eGbR' },
  { codes: ['221200'], label: 'UG (haftungsbeschränkt)' },
  { codes: ['112211'], label: 'GmbH & Co. KG' },
  { codes: ['111100'], label: 'OHG' },
  { codes: ['131000'], label: 'PartG' },
  { codes: ['222110'], label: 'AG', keywords: ['Aktiengesellschaft'] },
  { codes: ['251000'], label: 'eG (Genossenschaft)' },
  {
    codes: ['690000', '692320'],
    label: 'Ausländische Rechtsform',
    keywords: ['S.A.', 'SA', 'Sàrl', 'Sarl', 'Sagl', 'Société anonyme', 'Società anonima', 'Schweiz'],
  },
  { codes: ['211000'], label: 'e.V.', keywords: ['Verein'] },
  { codes: ['221120'], label: 'gGmbH' },
  { codes: ['112212'], label: 'UG & Co. KG' },
  { codes: ['260000', '268400'], label: 'Sonstige juristische Person', keywords: ['Stiftung'] },
  { codes: ['222200'], label: 'SE (Europäische AG)' },
  { codes: ['113000'], label: 'EWIV' },
  { codes: ['223100'], label: 'KGaA', keywords: ['Kommanditaktiengesellschaft'] },
  { codes: ['213100'], label: 'VVaG (Versicherungsverein)' },
  { codes: ['252000'], label: 'SCE (Europäische Genossenschaft)' },
];

/** Austrian (AT-FB) legal forms: backend code 900000 + the form number
 *  (backend/firmenbuch/legal_form.py), labelled as the backend labels them.
 *  The historic forms (OHG/KEG/OEG/Einzelkaufmann, 900014+) and the unknown
 *  900000 are not offered. */
export const AUSTRIAN_RECHTSFORM_OPTIONS: RechtsformOption[] = [
  { codes: ['900001'], label: 'GmbH (AT)' },
  { codes: ['900002'], label: 'AG (AT)' },
  { codes: ['900003'], label: 'FlexCo (AT)', keywords: ['Flexible Kapitalgesellschaft'] },
  { codes: ['900004'], label: 'KG (AT)' },
  { codes: ['900005'], label: 'OG (AT)', keywords: ['Offene Gesellschaft'] },
  { codes: ['900006'], label: 'e.U. (AT)', keywords: ['Eingetragenes Unternehmen'] },
  { codes: ['900007'], label: 'Genossenschaft (AT)' },
  { codes: ['900008'], label: 'Privatstiftung (AT)', keywords: ['Stiftung'] },
  { codes: ['900009'], label: 'Sparkasse (AT)' },
  { codes: ['900010'], label: 'VVaG (AT)' },
  { codes: ['900011'], label: 'SE (AT)' },
  { codes: ['900012'], label: 'EWIV (AT)' },
  { codes: ['900013'], label: 'Sonstiger Rechtsträger (AT)' },
];

/** Legal-form filter options. The Austrian forms join only once AT is a
 *  search scope (`COMPANY_COUNTRIES.AT.searchScope`) — before the backfill
 *  they could only ever return nothing. */
export const RECHTSFORM_OPTIONS: RechtsformOption[] = [
  ...GERMAN_RECHTSFORM_OPTIONS,
  ...(COMPANY_COUNTRIES.AT.searchScope ? AUSTRIAN_RECHTSFORM_OPTIONS : []),
];

/** Every legal form we can name — chip and alert labels must resolve a code
 *  from a URL or a stored alert even while its option is not offered. */
export const RECHTSFORM_LABEL_OPTIONS: RechtsformOption[] = [
  ...GERMAN_RECHTSFORM_OPTIONS,
  ...AUSTRIAN_RECHTSFORM_OPTIONS,
];
